import React, {useMemo} from 'react';
import { IonBadge, IonCard, IonCardContent } from '@ionic/react';
import './EveningCard.css';
import { Evening } from '../../model/Evening';
import { User } from '../../model/User';
import ProfilePicture from "../ProfilePicture";

type Props = {
    evening: Evening;
    onUserClick?: (user: User) => void;
    layout?: 'stacked' | 'row';
};

function formatDateNL(dateLike: Date | string): string {
    const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
    const s = d.toLocaleDateString('nl-NL', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function isWeekend(dateLike: Date | string): boolean {
    const d = dateLike instanceof Date ? dateLike : new Date(dateLike);
    const day = d.getDay();
    return day === 0 || day === 6 || day === 5;
}

const MAX_VISIBLE = 12;

const EveningCard: React.FC<Props> = ({ evening, onUserClick, layout = 'stacked' }) => {
    const { eveningDate, amountOfUsersPresent, presentUsers = [] } = evening;
    const weekend = isWeekend(eveningDate);
    const dayTypeLabel = weekend ? 'Weekend' : 'Doordeweeks';

    const { visibleUsers, hiddenCount } = useMemo(() => {
        if (presentUsers.length <= MAX_VISIBLE) {
            return { visibleUsers: presentUsers, hiddenCount: 0 };
        }
        return {
            visibleUsers: presentUsers.slice(0, MAX_VISIBLE),
            hiddenCount: presentUsers.length - MAX_VISIBLE,
        };
    }, [presentUsers]);

    const avatarSize = layout === 'stacked' ? 56 : 44;

    return (
        <IonCard className="evening-card">
            <IonCardContent className="evening-card-content">
                <div className="evening-card-header">
                    <div className="evening-card-date-block">
                        <div className="evening-card-title">Avond</div>
                        <div className="evening-card-date">{formatDateNL(eveningDate)}</div>
                        <div className="evening-card-sub">
                            <span role="img" aria-label="cheers">🍻</span>
                            <span>{amountOfUsersPresent} {amountOfUsersPresent === 1 ? 'aanwezige' : 'aanwezigen'}</span>
                        </div>
                    </div>
                    <IonBadge className="evening-card-badge">{dayTypeLabel}</IonBadge>
                </div>

                <div className="evening-card-divider" />

                <div className={`evening-users-grid ${layout === 'stacked' ? 'stacked' : 'row'}`}>
                    {visibleUsers.map((u, idx) => (
                        <button
                            key={u.id ?? `${u.name}-${idx}`}
                            className={`evening-user ${layout}`}
                            type="button"
                            onClick={() => onUserClick?.(u)}
                            title={u.name}
                        >
                            <ProfilePicture
                                profilePicUrl={u.profilePictureUrl !== undefined ? `${process.env.REACT_APP_STORAGE}${u.profilePictureUrl}` : `${process.env.REACT_APP_STORAGE}default-profile.jpg`}
                                fullName={u.name}
                                showEditable={false}
                                width={avatarSize}
                                height={avatarSize}
                            />
                            <div className="evening-user-name">{u.name}</div>
                        </button>
                    ))}

                    {hiddenCount > 0 && (
                        <div className={`evening-user ${layout} evening-user-more`} aria-hidden="true">
                            <div className="evening-user-more-avatar">+{hiddenCount}</div>
                            <div className="evening-user-name">meer</div>
                        </div>
                    )}
                </div>
            </IonCardContent>
        </IonCard>
    );
};

export default EveningCard;
